import { useState } from 'react';
import { X, Send, Mail, DollarSign, Clock, FileText, AlertCircle } from 'lucide-react';
import { Quotation } from '../../types/maintenance';

interface SubmitForApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  quotations: Quotation[];
  loading?: boolean;
}

export default function SubmitForApprovalModal({
  isOpen,
  onClose,
  onConfirm,
  quotations,
  loading = false
}: SubmitForApprovalModalProps) {
  const [error, setError] = useState('');

  const handleClose = () => {
    setError('');
    onClose();
  };

  if (!isOpen) return null;

  const handleSubmit = async () => {
    setError('');
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao submeter para aprovação.');
    }
  };

  const sortedQuotations = [...quotations].sort((a, b) => a.quotedValue - b.quotedValue);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Send className="h-5 w-5 mr-2 text-purple-600" />
              Submeter para Aprovação
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
              disabled={loading}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <p className="text-sm text-gray-600 mb-4 flex items-start">
            <Mail className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0 text-gray-400" />
            <span>
              O CFO irá receber um email com estas cotações e um link directo para aprovar.
            </span>
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Quotations preview */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Cotações actualmente cadastradas ({sortedQuotations.length})
            </h4>
            <div className="border rounded-lg overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">#</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Fornecedor</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-500">Valor</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-500">Observações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedQuotations.map((q, idx) => (
                    <tr key={q.id}>
                      <td className="px-3 py-2 text-gray-500">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{q.partnerName}</div>
                        <div className="text-xs text-gray-500">{q.contactEmail}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900 whitespace-nowrap">
                        <DollarSign className="inline h-3 w-3 mr-0.5 text-gray-400" />
                        {Number(q.quotedValue).toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-500">
                        {q.deliveryTime && (
                          <span className="flex items-center mb-0.5">
                            <Clock className="h-3 w-3 mr-1" />
                            {q.deliveryTime}
                          </span>
                        )}
                        {q.notes && (
                          <span className="flex items-start">
                            <FileText className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                            <span>{q.notes}</span>
                          </span>
                        )}
                        {!q.deliveryTime && !q.notes && <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || sortedQuotations.length === 0}
              className="px-4 py-2 text-sm font-medium text-white bg-purple-600 border border-transparent rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <span>A submeter...</span>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submeter para Aprovação
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}